import React, { useState, useEffect, useCallback } from 'react';
import ValuationDetailsModal from './ValuationDetailsModal';
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Row,
    Form,
    FormGroup,
    Label,
    Input,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import Pagination from '../../../components/Pagination/Pagination';
import { enqueueSnackbar } from 'notistack';
import {
    getAllMortgageValuations,
    deleteMortgageValuation,
    getAllClients,
    getAllProjects,
} from '../../../services/ValitanApiService';
import { ConfirmDialog } from 'primereact/confirmdialog';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

const MortgageValuationListTables = () => {
    const [valuations, setValuations] = useState([]);
    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedValuation, setSelectedValuation] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const toggleModal = () => setModalOpen(!modalOpen);

    const [filters, setFilters] = useState({
        projectId: '',
        clientId: '',
        status: '',
        startDate: '',
        endDate: '',
    });

    const [paginationInfo, setPaginationInfo] = useState({
        pageNumber: 0,
        pageSize: 10,
        totalElements: 0,
        totalPages: 0,
    });

    // Normalize data for table
    const normalize = (v) => ({
        ...v,
        projectName: v.project?.name || '-',
        clientName: v.client?.name || '-',
        valuerName: v.valuer
            ? `${v.valuer.first_name} ${v.valuer.last_name}`
            : 'N/A',
        status: v.status || '-',
        valuationDate: v.valuation_date || '-',
    });

    // Fetch valuations from API
    const fetchValuations = useCallback(
        async (pageNumber = 0, pageSize = 10) => {
            try {
                setLoading(true);
                setError(null);

                const response = await getAllMortgageValuations({
                    projectId: filters.projectId || undefined,
                    clientId: filters.clientId || undefined,
                    status: filters.status || undefined,
                    startDate: filters.startDate || undefined,
                    endDate: filters.endDate || undefined,
                    page: pageNumber,
                    size: pageSize,
                });

                const { data } = response;
                const result = data.data; // backend wraps inside "data"

                setValuations((result.content || []).map(normalize));
                setPaginationInfo({
                    pageNumber: result.number,
                    pageSize: result.size,
                    totalElements: result.total_elements,
                    totalPages: result.total_pages,
                });
            } catch (err) {
                console.error(err);
                setError(err);
            } finally {
                setLoading(false);
            }
        },
        [filters]
    );

    // Fetch dropdown data (clients + projects)
    const fetchDropdownData = useCallback(async () => {
        try {
            const [clientsRes, projectsRes] = await Promise.all([
                getAllClients(),
                getAllProjects(),
            ]);

            setClients(
                Array.isArray(clientsRes.data?.data?.content)
                    ? clientsRes.data.data.content
                    : []
            );

            setProjects(
                Array.isArray(projectsRes.data?.content)
                    ? projectsRes.data.content
                    : []
            );
        } catch (err) {
            console.error('Error fetching dropdown data', err);
            setClients([]);
            setProjects([]);
        }
    }, []);

    useEffect(() => {
        document.title = 'Mortgage Valuations | Valitan';
    }, []);

    useEffect(() => {
        fetchValuations(0, paginationInfo.pageSize);
        fetchDropdownData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchValuations]);

    const handlePreviousPage = () => {
        if (paginationInfo.pageNumber > 0) {
            fetchValuations(
                paginationInfo.pageNumber - 1,
                paginationInfo.pageSize
            );
        }
    };

    const handleNextPage = () => {
        if (paginationInfo.pageNumber < paginationInfo.totalPages - 1) {
            fetchValuations(
                paginationInfo.pageNumber + 1,
                paginationInfo.pageSize
            );
        }
    };

    const handlePageChange = (pageNumber, pageSize) =>
        fetchValuations(pageNumber, pageSize);

    const handlePageSizeChange = (newPageSize) =>
        fetchValuations(0, newPageSize);

    const handleDeleteValuation = async (id) => {
        try {
            const response = await deleteMortgageValuation(id);
            if (response.status === 200 || response.status === 204) {
                enqueueSnackbar('Valuation deleted successfully', {
                    variant: 'success',
                });
                fetchValuations(paginationInfo.pageNumber, paginationInfo.pageSize);
            } else {
                enqueueSnackbar('Failed to delete valuation', { variant: 'error' });
            }
        } catch (err) {
            console.error('Error deleting valuation:', err);
            enqueueSnackbar('An error occurred while deleting', {
                variant: 'error',
            });
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleApplyFilters = () => {
        fetchValuations(0, paginationInfo.pageSize);
    };

    const handleResetFilters = () => {
        setFilters({
            projectId: '',
            clientId: '',
            status: '',
            startDate: '',
            endDate: '',
        });
        fetchValuations(0, paginationInfo.pageSize);
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs
                        title="Valuations management"
                        breadcrumbItem="Mortgage Valuations"
                    />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <h4 className="card-title mb-0">
                                        <Link to="/add-mortgage-valuation">
                                            <Button
                                                color="primary"
                                                className="add-btn float-end"
                                                id="create-btn"
                                            >
                                                <i className="ri-add-line align-bottom me-1" /> Add
                                            </Button>
                                        </Link>
                                    </h4>
                                </CardHeader>

                                <CardBody>
                                    {/* 🔍 Filter Bar */}
                                    <Form className="mb-3">
                                        <Row className="g-3">
                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label for="clientId">Client</Label>
                                                    <Input
                                                        type="select"
                                                        name="clientId"
                                                        value={filters.clientId}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All Clients</option>
                                                        {clients.map((c) => (
                                                            <option key={c.id} value={c.id}>
                                                                {c.name}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                </FormGroup>
                                            </Col>

                                            <Col md={3}>
                                                <FormGroup>
                                                    <Label for="projectId">Project</Label>
                                                    <Input
                                                        type="select"
                                                        name="projectId"
                                                        value={filters.projectId}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All Projects</option>
                                                        {projects.map((p) => (
                                                            <option key={p.id} value={p.id}>
                                                                {p.name}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                </FormGroup>
                                            </Col>

                                            <Col md={2}>
                                                <FormGroup>
                                                    <Label for="status">Status</Label>
                                                    <Input
                                                        type="select"
                                                        name="status"
                                                        value={filters.status}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All</option>
                                                        <option value="DRAFT">DRAFT</option>
                                                        <option value="SUBMITTED">SUBMITTED</option>
                                                        <option value="APPROVED">APPROVED</option>
                                                        <option value="REJECTED">REJECTED</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>

                                            <Col md={2}>
                                                <FormGroup>
                                                    <Label for="startDate">Start Date</Label>
                                                    <Input
                                                        type="date"
                                                        name="startDate"
                                                        value={filters.startDate}
                                                        onChange={handleFilterChange}
                                                    />
                                                </FormGroup>
                                            </Col>

                                            <Col md={2}>
                                                <FormGroup>
                                                    <Label for="endDate">End Date</Label>
                                                    <Input
                                                        type="date"
                                                        name="endDate"
                                                        value={filters.endDate}
                                                        onChange={handleFilterChange}
                                                    />
                                                </FormGroup>
                                            </Col>

                                            <Col md={12} className="d-flex justify-content-end">
                                                <Button
                                                    color="primary"
                                                    className="me-2"
                                                    onClick={handleApplyFilters}
                                                >
                                                    Apply
                                                </Button>
                                                <Button
                                                    color="secondary"
                                                    onClick={handleResetFilters}
                                                >
                                                    Reset
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Form>

                                    {/* Table */}
                                    {loading ? (
                                        <div>Loading...</div>
                                    ) : error ? (
                                        <div>Error fetching valuations: {error.message}</div>
                                    ) : (
                                        <div className="table-responsive table-card mt-3 mb-1">
                                            <table
                                                className="table align-middle table-nowrap"
                                                id="valuationTable"
                                            >
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Reference</th>
                                                        <th>Project</th>
                                                        <th>Client</th>
                                                        <th>Valuer</th>
                                                        <th>Status</th>
                                                        <th>Valuation Date</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {valuations.length > 0 ? (
                                                        valuations.map((v, idx) => (
                                                            <tr key={v.id}>
                                                                <td>
                                                                    {paginationInfo.pageNumber *
                                                                        paginationInfo.pageSize +
                                                                        idx +
                                                                        1}
                                                                </td>
                                                                <td>{v.valuation_ref}</td>
                                                                <td>{v.projectName}</td>
                                                                <td>{v.clientName}</td>
                                                                <td>{v.valuerName}</td>
                                                                <td>
                                                                    <span
                                                                        className={`badge ${v.status === 'SUBMITTED'
                                                                                ? 'bg-success'
                                                                                : v.status === 'DRAFT'
                                                                                    ? 'bg-warning'
                                                                                    : 'bg-secondary'
                                                                            }`}
                                                                    >
                                                                        {v.status}
                                                                    </span>
                                                                </td>
                                                                <td>{v.valuationDate}</td>
                                                                <td>
                                                                    <div className="btn-group" role="group">
                                                                        <Button
                                                                            color="primary"
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                setSelectedValuation(v);
                                                                                setModalOpen(true);
                                                                            }}
>
                                                                        <i className="ri-eye-line" />
                                                                    </Button>
                                                                        <Link
                                                                            to={`/update-mortgage-valuation/${v.id}`}
                                                                            className="btn btn-primary btn-sm"
                                                                        >
                                                                            <i className="ri-edit-box-line" />
                                                                        </Link>
                                                                        <Button
                                                                            color="danger"
                                                                            size="sm"
                                                                            onClick={() =>
                                                                                handleDeleteValuation(v.id)
                                                                            }
                                                                        >
                                                                            <i className="ri-delete-bin-line" />
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="8" className="text-center">
                                                                No records found.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    <Pagination
                                        paginationInfo={paginationInfo}
                                        handleNextPage={handleNextPage}
                                        handlePreviousPage={handlePreviousPage}
                                        onPageChange={handlePageChange}
                                        onPageSizeChange={handlePageSizeChange}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
                <ConfirmDialog />
                <ValuationDetailsModal
                    isOpen={modalOpen}
                    toggle={toggleModal}
                    valuation={selectedValuation}
                />
            </div>
        </React.Fragment>
    );
};

export default MortgageValuationListTables;
